'use client'

import SectionBadge from '@/components/ui/section-badge'
import { 
  Accordion, 
  AccordionItem, 
  AccordionButton, 
  AccordionPanel 
} from '@/components/animate-ui/headless/accordion'
import { cn } from '@/lib/utils'

export interface FAQItem {
  question: string
  answer: string
}

interface FAQSectionProps {
  title?: string
  subtitle?: string
  badge?: string
  showBadge?: boolean
  items: FAQItem[]
  className?: string
  mode?: 'light' | 'dark'
}

export default function FAQSection({
  title = "Get answers to the most commonly asked questions",
  subtitle,
  badge = "FAQ",
  showBadge = true,
  items,
  className = "",
  mode = "light"
}: FAQSectionProps) {
  return (
    <div id="faq" className={`mx-auto mt-24 flex w-full max-w-4xl flex-col gap-12 ${className}`}>
      <div className="mx-auto mb-14 flex w-full flex-col items-center gap-4 md:w-[90%]">
        {showBadge && (
          <SectionBadge className={cn("border-border", mode === 'light' ? 'text-foreground bg-background' : 'text-white bg-primary')}>
            {badge}
          </SectionBadge>
        )}
        <h2 className={cn("text-center font-semibold text-3xl md:text-5xl", mode === 'light' ? 'text-foreground' : 'text-white')}>
          {title}
        </h2>
        {subtitle && (
          <p className="text-center text-muted-foreground text-lg max-w-2xl">
            {subtitle}
          </p>
        )}
      </div>
      
      <Accordion className="mx-auto flex w-full max-w-4xl flex-col gap-4">
        {items.map((faq, index) => (
          <AccordionItem 
            key={index}
            className="group/item rounded-[100px] border bg-background px-8 py-2 transition-[border-radius] duration-100 ease-in-out data-[open]:rounded-4xl"
          >
            <AccordionButton className="group/accordion flex flex-1 items-center justify-between gap-4 rounded-md py-4 font-medium outline-none disabled:pointer-events-none focus-visible:border-ring disabled:opacity-50 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus:ring-0 text-start text-base transition-all duration-200 ease-in-out hover:no-underline">
              <span className="line-clamp-1 md:line-clamp-none group-data-[open]/item:line-clamp-none">
                {faq.question}
              </span>
            </AccordionButton>
            
            <AccordionPanel className="text-muted-foreground">
              {faq.answer}
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  )
}
